<template>
  <tr class="ce-productDataField">
    <td>
      <label>
        {{ field.title }}

        <fa-icon
          v-if="accessLevelForIndustry && accessLevelForIndustry.code === 'internal'"
          transform="up-6 shrink-4"
          icon="lock" />
      </label>
    </td>

    <td v-html="valueWithUnit" />
  </tr>
</template>

<script>
import { mapState } from 'vuex';
import { translate } from 'utils/i18n';

export default {
  props: {
    field: {
      type: Object,
      required: true
    }
  },
  computed: {
    ...mapState(['activeIndustry']),
    hasSimpleValue() {
      const type = typeof this.field.value;

      return ['string', 'number', 'boolean'].indexOf(type) > -1;
    },
    hasArrayValue() {
      return Array.isArray(this.field.value);
    },
    hasKeyValuePairsValue() {
      if (this.field.value && typeof this.field.value === 'object') {
        const keys = Object.keys(this.field.value);
        const values = Object.values(this.field.value);

        if (keys.length > 0
            && typeof keys[0] === 'string'
            && values.length > 0
            && ['string', 'number', 'boolean'].indexOf(typeof values[0]) > -1) {
          return true;
        }
      }

      return false;
    },
    hasOptions() {
      return this.field.options && typeof this.field.options === 'object';
    },
    value() {
      if (this.hasSimpleValue) {
        if (typeof this.field.value === 'string'
          && this.hasOptions
          && typeof this.field.options[this.field.value] !== 'undefined') {
          return this.field.options[this.field.value];
        }

        if (typeof this.field.value === 'boolean') {
          return this.field.value ? translate('filter.boolFilter.yes') : translate('filter.boolFilter.no');
        }

        return this.field.value;
      }

      if (this.field.dataType === 'nested_list') {
        if (!this.hasOptions) {
          return null;
        }

        const response = [];

        this.field.value.forEach((item) => {
          if (item.value && this.field.options[item.key]) {
            let { value } = item;
            const optionConfig = (() => {
              if (this.field.optionsConfig) {
                return this.field.optionsConfig.find((o) => o.code === item.key);
              }

              return null;
            })();

            if (optionConfig && optionConfig.unit) {
              value = `<span class="ce-fieldWithUnit"><span>${value}</span><span class="ce-fieldUnit">${optionConfig.unit}</span></span>`;
            }

            response.push(
              `<p><span class="font-bold">${this.field.options[item.key]}:</span><br>${value}</p>`
            );
          }
        });

        return response.join('');
      }

      if (this.hasArrayValue) {
        const response = [];
        this.field.value.forEach((code) => {
          if (typeof code === 'string'
            && this.hasOptions
            && this.field.options[code] !== 'undefined') {
            response.push(this.field.options[code]);
          } else if (typeof code === 'object'
            && typeof code.key === 'string'
            && this.hasOptions
            && this.field.options[code.key] !== 'undefined') {
            if (code.hierarchy) {
              response.push(
                `<span class="nowrap">${this.field.options[code.key]} <span class="ce-hierarchyIndicator ${code.hierarchy}"></span></span>`
              );
            } else {
              response.push(this.field.options[code.key]);
            }
          }
        });

        return response.join(', ');
      }

      if (this.field.code === 'particle_size_range_95') {
        return this.field.value.raw;
      }

      if (this.hasKeyValuePairsValue) {
        const response = [];
        Object.keys(this.field.value).forEach((code) => {
          let label = code;

          if (typeof code === 'string'
          && this.hasOptions
          && this.field.options[code] !== 'undefined') {
            label = this.field.options[code];
          }

          response.push(`<span class="font-bold">${label}:</span><br> ${this.field.value[code]}`);
        });

        return `<p>${response.join('</p><p>')}</p>`;
      }

      return null;
    },
    valueWithUnit() {
      const { value } = this;

      return this.field.unit ? `${value} ${this.field.unit}` : value;
    },
    accessLevelForIndustry() {
      if (this.field.accessLevel && Array.isArray(this.field.accessLevel)) {
        return this.field.accessLevel.find((acl) => acl.industry === this.activeIndustry);
      }

      return null;
    }
  }
};
</script>

<style lang="less">
  @import (reference) '~styles/includes/base';

  .ce-productDataField {
    td {
      .ce-hierarchyIndicator {
        position: relative;
        top: -5px;
      }
    }
  }
</style>
